import { useState, useEffect, useRef } from 'react';
import { bulkCreateRelationships } from '../../api/analytics';

/**
 * StandardRelationshipSuggestionsModal
 *
 * Props:
 *   pairings   - array of { child_ds, parent_ds, suggestions: [SuggestionItem] }
 *   onCreated  - called after successful create (no args); caller should refresh
 *   onClose    - called when modal is dismissed without creating
 */
export default function StandardRelationshipSuggestionsModal({ pairings, onCreated, onClose }) {
  // Build initial checked state: checked by default unless already_exists
  const buildInitialChecked = () => {
    const state = {};
    for (const pairing of pairings) {
      for (const s of pairing.suggestions) {
        const key = `${pairing.child_ds.data_source_id}|${pairing.parent_ds.data_source_id}|${s.relationship_name}`;
        state[key] = !s.already_exists;
      }
    }
    return state;
  };

  const [checked, setChecked] = useState(buildInitialChecked);
  const [loading, setLoading] = useState(false);
  const [networkError, setNetworkError] = useState(null);
  // itemResults: key → { success: bool, error: string|null }
  const [itemResults, setItemResults] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const onCreatedRef = useRef(onCreated);
  // Keep ref current so the timeout closure doesn't capture a stale callback.
  onCreatedRef.current = onCreated;

  // Auto-close 1s after all items succeed; cleans up if modal unmounts first.
  useEffect(() => {
    if (!submitted) return;
    if (Object.values(itemResults).some(r => !r.success)) return;
    const id = setTimeout(() => onCreatedRef.current(), 1000);
    return () => clearTimeout(id);
  }, [submitted, itemResults]);

  const allAlreadyExist = pairings.every(p => p.suggestions.every(s => s.already_exists));

  const checkedCount = Object.values(checked).filter(Boolean).length;

  const toggleCheck = (key) => {
    setChecked(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleCreate = async () => {
    setNetworkError(null);
    setLoading(true);
    setItemResults({});

    try {
      // For each pairing, collect checked (non-already-existing) items and submit
      const allResults = {};

      for (const pairing of pairings) {
        const childDsId = pairing.child_ds.data_source_id;
        const parentDsId = pairing.parent_ds.data_source_id;

        const toCreate = pairing.suggestions.filter(s => {
          const key = `${childDsId}|${parentDsId}|${s.relationship_name}`;
          return checked[key] && !s.already_exists;
        });

        if (toCreate.length === 0) continue;

        const relationships = toCreate.map(s => ({
          relationship_name: s.relationship_name,
          parent_to_child_name: s.parent_to_child_name || '',
          child_object: s.source_object,
          child_field: s.source_field,
          parent_object: s.target_object,
          parent_field: s.target_field,
          case_insensitive: s.case_insensitive,
        }));

        const result = await bulkCreateRelationships(childDsId, parentDsId, relationships);

        // Mark per-item results
        for (const s of toCreate) {
          const key = `${childDsId}|${parentDsId}|${s.relationship_name}`;
          const itemError = result.errors?.find(e => e.relationship_name === s.relationship_name);
          allResults[key] = {
            success: !itemError,
            error: itemError ? itemError.error : null,
          };
        }
      }

      setItemResults(allResults);
      setSubmitted(true);

      const anyError = Object.values(allResults).some(r => !r.success);
      if (!anyError) {
        // All succeeded — will auto-close via the useEffect below
      }
    } catch (err) {
      setNetworkError(err.message || 'Network error — please try again');
    } finally {
      setLoading(false);
    }
  };

  const createBtnLabel = loading
    ? 'Creating\u2026'
    : `Create ${checkedCount} Relationship${checkedCount === 1 ? '' : 's'}`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      onClick={onClose}
    >
      <div
        data-testid="standard-rel-modal"
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="std-rel-modal-title"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-5 border-b border-gray-200 flex items-center justify-between">
          <h2 id="std-rel-modal-title" className="text-lg font-bold text-gray-900">
            Standard Relationship Setup
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5 space-y-6">
          {networkError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {networkError}
            </div>
          )}

          {allAlreadyExist ? (
            <div className="text-center py-6">
              <p className="text-gray-700 font-medium mb-1">All relationships are already set up</p>
              <p className="text-sm text-gray-500">No new relationships need to be created.</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600">
                Select the standard relationships to create. These are based on native Salesforce ID
                fields stored by HubSpot when the integration is active.
              </p>

              {pairings.map(pairing => {
                const childDsId = pairing.child_ds.data_source_id;
                const parentDsId = pairing.parent_ds.data_source_id;

                return (
                  <div key={`${childDsId}:${parentDsId}`} className="space-y-3">
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                      <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">
                        {pairing.child_ds.name}
                      </span>
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                        {pairing.parent_ds.name}
                      </span>
                    </div>

                    <div className="space-y-2">
                      {pairing.suggestions.map(s => {
                        const key = `${childDsId}|${parentDsId}|${s.relationship_name}`;
                        const result = itemResults[key];
                        const isDisabled = s.already_exists || loading;

                        return (
                          <label
                            key={s.relationship_name}
                            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${
                              s.already_exists
                                ? 'bg-gray-50 border-gray-200 opacity-60 cursor-not-allowed'
                                : 'bg-white border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              data-testid={`suggestion-checkbox-${s.relationship_name}`}
                              checked={checked[key] || false}
                              onChange={() => !isDisabled && toggleCheck(key)}
                              disabled={isDisabled}
                              className="mt-0.5"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm text-gray-900">
                                  {s.relationship_name}
                                </span>
                                {s.already_exists && (
                                  <span className="text-xs text-gray-500 italic">already set up</span>
                                )}
                                {result?.success && (
                                  <span className="text-green-600 text-sm font-bold" aria-label="Created successfully">
                                    &#10003;
                                  </span>
                                )}
                                {result && !result.success && (
                                  <span className="text-red-600 text-xs">{result.error}</span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 mt-0.5">{s.description}</p>
                              <p className="text-xs text-gray-400 font-mono mt-0.5">
                                {s.source_object}.{s.source_field} &rarr; {s.target_object}.{s.target_field}
                              </p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        <div className="p-5 border-t border-gray-100 flex justify-end gap-3">
          {allAlreadyExist ? (
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Close
            </button>
          ) : (
            <>
              <button
                data-testid="standard-rel-skip-btn"
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Skip
              </button>
              <button
                data-testid="standard-rel-create-btn"
                onClick={handleCreate}
                disabled={loading || checkedCount === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createBtnLabel}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
